import { createContext, useContext, useState, useCallback, useRef } from "react";

const MatchContext = createContext(null);

export function MatchProvider({ children }) {
  const [selectedPair, setSelectedPair] = useState({ left: null, right: null });
  const [completedPairs, setCompletedPairs] = useState(new Set());
  const [wrongPair, setWrongPair] = useState(null);
  const [arrows, setArrows] = useState([]);
  
  // Usar ref para posiciones de círculos para evitar re-renders innecesarios
  const circlePositionsRef = useRef({ left: new Map(), right: new Map() });

  const selectLeft = useCallback((id) => {
    setSelectedPair((prev) => ({ ...prev, left: id }));
  }, []);

  const selectRight = useCallback((id) => {
    setSelectedPair((prev) => ({ ...prev, right: id }));
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedPair({ left: null, right: null });
  }, []);

  const addCompletedPair = useCallback((id) => {
    setCompletedPairs((prev) => new Set([...prev, id]));
  }, []);

  const isCompleted = useCallback((id) => {
    return completedPairs.has(id);
  }, [completedPairs]);

  const showWrongPair = useCallback((leftId, rightId) => {
    setWrongPair({ left: leftId, right: rightId });
    setTimeout(() => setWrongPair(null), 1000);
  }, []);

  const addArrow = useCallback((id) => {
    const leftPos = circlePositionsRef.current.left.get(id);
    const rightPos = circlePositionsRef.current.right.get(id);
    
    if (leftPos && rightPos) {
      setArrows((prev) => [...prev, { id, start: leftPos, end: rightPos }]);
    }
  }, []);

  const clearArrows = useCallback(() => {
    setArrows([]);
  }, []);

  const updateCirclePosition = useCallback((id, side, position) => {
    circlePositionsRef.current[side].set(id, position);
  }, []);

  const updateAllCirclePositions = useCallback((side, positions) => {
    circlePositionsRef.current[side] = new Map(
      positions.map((pos) => [pos.id, { x: pos.x, y: pos.y }])
    );
    
    // Actualizar flechas existentes con nuevas posiciones
    setArrows((prevArrows) => {
      if (prevArrows.length === 0) return prevArrows;
      
      const updatedArrows = prevArrows.map((arrow) => {
        const leftPos = circlePositionsRef.current.left.get(arrow.id);
        const rightPos = circlePositionsRef.current.right.get(arrow.id);
        return leftPos && rightPos
          ? { id: arrow.id, start: leftPos, end: rightPos }
          : arrow;
      });
      
      // Siempre retornar las arrows actualizadas para forzar re-render
      return [...updatedArrows];
    });
  }, []);

  const getCirclePosition = useCallback((id, side) => {
    return circlePositionsRef.current[side].get(id);
  }, []);

  const resetMatch = useCallback(() => {
    setSelectedPair({ left: null, right: null });
    setCompletedPairs(new Set());
    setWrongPair(null);
    setArrows([]);
  }, []);

  const value = {
    selectedPair,
    completedPairs,
    wrongPair,
    arrows,
    selectLeft,
    selectRight,
    clearSelection,
    addCompletedPair,
    isCompleted,
    showWrongPair,
    addArrow,
    clearArrows,
    updateCirclePosition,
    updateAllCirclePositions,
    getCirclePosition,
    resetMatch,
  };

  return (
    <MatchContext.Provider value={value}>{children}</MatchContext.Provider>
  );
}

export function useMatch() {
  const context = useContext(MatchContext);
  if (!context) {
    throw new Error("useMatch must be used inside <MatchProvider>");
  }
  return context;
}
